import {FC, useState, useEffect, useContext, useRef, useCallback} from "react";
import {makeStyles} from "tss-react/mui";
import {
	Box,
	Button,
	CircularProgress,
	Stack,
	Typography,
	Alert,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	OutlinedInput,
	Select
} from "@mui/material";
import {useNavigate, useParams, useBlocker} from "react-router-dom";
import {IconContext} from "@buildwitt/shapeshift-identity";
import type {IEntity} from "@buildwitt/shapeshift-types";
import {
	useGetAdminCourse,
	useCreateCourse,
	useUpdateCourse
	// useDeleteCourse — commented out in favor of publish/unpublish
} from "@/app2/shared/data/repos/courses";
import {corePaths} from "@buildwitt/shapeshift-types";
import {AdminInputText} from "@/pages/ManagerView/ContentCreation/AdminInputText";
import {AdminInputSelect} from "@/pages/ManagerView/ContentCreation/AdminInputSelect";
import {AdminInputSwitch} from "@/pages/ManagerView/ContentCreation/AdminInputSwitch";
import ImageSelector from "@/pages/ManagerView/ContentCreation/ImageSelector";
import AdminCourseCurriculum, {IFlatListItem} from "./AdminCourseEditor/AdminCourseCurriculum";
import {IDropzoneFile} from "@buildwitt/shapeshift-common/v1/fileDropzone/fileDropzoneFile";
import {CourseResponsePrivacy, getApiErrorMessage, type CourseResponse} from "@/app2/shared/data";
import {AdminHeader} from "@/app2/pages/ManagerView/ContentCreation/AdminHeader";
import ConfirmationDialog from "@/components/common/dialogs/ConfirmationDialog";
import {IconAdminCourses} from "@/icons/IconAdminCourses";
import {Link} from "react-router-dom";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {LocaleContext, resolveLocale} from "@/app2/shared/contexts";
import {jobCategories, workCategories} from "@/constants";
// import DeleteIcon from "@mui/icons-material/Delete"; — commented out

type AdminHeaderEntity = IEntity & {title?: string};

const useStyles = makeStyles()((theme) => ({
	root: {
		minHeight: "100vh",
		paddingBottom: theme.spacing(4)
	},
	formContainer: {
		maxWidth: "980px",
		margin: "0 auto",
		padding: theme.spacing(0, 3)
	},
	formFieldset: {
		borderRadius: "4px",
		marginBottom: theme.spacing(2),
		"&.Mui-expanded": {
			margin: `0 0 ${theme.spacing(2)} 0`
		}
	},
	formFieldsetSummary: {
		padding: theme.spacing(0, 3, 0.5),
		".MuiAccordionSummary-content": {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(1)
		}
	},
	subtitle: {
		opacity: 0.7
	},
	formFieldsetDetails: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(4),
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(3),
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(3)
	},
	fieldsetSettings: {
		paddingTop: theme.spacing(3),
		paddingBottom: theme.spacing(6)
	},
	formFieldsetNumber: {
		lineHeight: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		height: "24px",
		marginRight: theme.spacing(2),
		color: theme.palette.primary.contrastText,
		borderRadius: "100%",
		aspectRatio: "1 / 1",
		backgroundColor: theme.palette.primary.main,
		fontSize: "14px",
		fontWeight: 600
	},
	divider: {
		borderTop: `1px solid ${theme.palette.grey[200]}`,
		width: "100%",
		height: "1px"
	},
	actions: {
		position: "sticky",
		bottom: 0,
		backgroundColor: theme.palette.background.paper,
		borderTop: `1px solid ${theme.palette.grey[200]}`,
		padding: theme.spacing(2, 3),
		display: "flex",
		gap: theme.spacing(2),
		justifyContent: "flex-start",
		zIndex: 100,
		boxShadow: "0 -2px 8px rgba(0,0,0,0.1)"
	},
	loadingContainer: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		minHeight: "400px"
	},
	cancelBtn: {
		border: "none"
	},
	select: {
		fieldset: {
			borderColor: theme.palette.grey[200]
		},
		"& svg path": {
			fill:  theme.palette.grey[200]
		}
	}
}));

const contentTypes = [
	"Job Training",
	"Safety",
	"Personal Development",
	"Professional Development"
];

/** Aligned with improve-api PostCourseBody / PutCourseBody (@MaxLength). */
const COURSE_TITLE_MAX_LENGTH = 255;
const COURSE_DESCRIPTION_MAX_LENGTH = 5000;

/** Order-sensitive key for curriculum dirty comparison. */
function curriculumKey(items: IFlatListItem[]): string {
	return items.map((i) => i.moduleId).join("|");
}

/** Map UI curriculum to improve-api payload; */
function toApiCurriculum(items: IFlatListItem[]): Array<{moduleId: number; type: string; title?: string; level: number; progression: boolean}> {
	return items.map((item) => ({
		moduleId: item.moduleId,
		type: corePaths.lesson,
		title: item.title,
		level: 0,
		progression: true
	}));
}

interface CourseFormData {
	title: string;
	description: string;
	contentType: string;
	jobCategory: string[];
	workCategory: string[];
	linearProgression: boolean;
	privacy: "cohort" | "global";
	curriculum: IFlatListItem[];
}

const defaultFormData: CourseFormData = {
	title: "",
	description: "",
	// Explicit default so the value matches the UI expectation and the API always receives a real selection.
	contentType: "Job Training",
	jobCategory: [],
	workCategory: [],
	linearProgression: false,
	privacy: "cohort",
	curriculum: []
};

/** Editable course fields excluding curriculum, which is hydrated/tracked separately. */
type CourseFormFields = Omit<CourseFormData, "curriculum">;

/**
 * Single source of truth for mapping a loaded course onto editable form fields.
 * New course fields go here only — form hydration, the unsaved-changes baseline,
 * and the dirty check all derive from this.
 */
function formFieldsFromCourse(course: CourseResponse): CourseFormFields {
	return {
		title: course.title ?? "",
		description: course.description ?? "",
		contentType: course.contentType ?? "",
		jobCategory: course.jobCategory ?? [],
		workCategory: course.workCategory ?? [],
		linearProgression: course.linearProgression ?? false,
		privacy: course.privacy ?? CourseResponsePrivacy.cohort
	};
}

/** Drops curriculum from form data so the result can be compared against a {@link CourseFormFields} baseline. */
function pickFormFields(data: CourseFormData): CourseFormFields {
	const {curriculum, ...fields} = data;
	return fields;
}

export const AdminCourseEditor: FC = () => {
	const {classes, cx} = useStyles();
	const navigate = useNavigate();
	const {id} = useParams<{id?: string}>();
	const courseId = id ? parseInt(id) : undefined;
	const isEditMode = !!courseId;
	const {icon} = useContext(IconContext);
	const {t} = useSSTranslation();
	const [{locale: appLocale}] = useContext(LocaleContext);
	const {successSnackbar, errorSnackbar, closeSnackbar} = useCustomSnackbar();

	// Form state
	const [formData, setFormData] = useState<CourseFormData>(defaultFormData);
	const [files, setFiles] = useState<{featuredImage?: IDropzoneFile[] | null}>({});
	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
	const [expandedSection, setExpandedSection] = useState<string | false>("1");
	const validationAlertRef = useRef<HTMLDivElement>(null);
	const formHydratedForCourseIdRef = useRef<number | undefined>(undefined);

	// Unsaved-changes guard
	const [openExitPageModal, setOpenExitPageModal] = useState(false);
	// Bypasses the useBlocker check for intentional navigation (Cancel button, post-create redirect)
	const forceExitPageRef = useRef(false);
	const initialDataRef = useRef<CourseFormFields>(pickFormFields(defaultFormData));
	// Curriculum is hydrated asynchronously by the child, so its baseline is captured on the first onChange.
	const initialCurriculumKeyRef = useRef<string>("");
	const curriculumBaselineCapturedRef = useRef(false);

	// React Query hooks
	const {data: course, isLoading, error} = useGetAdminCourse(courseId);
	const createMutation = useCreateCourse();
	const updateMutation = useUpdateCourse(courseId ?? 0);
	const saveButtonLabel = isEditMode && !!course?.publishedAt ? "Save" : "Save As Draft";
	// const deleteMutation = useDeleteCourse(); — commented out

	// Reset form when navigating to create mode (no courseId) without a full remount
	useEffect(() => {
		if (!courseId) {
			setFormData(defaultFormData);
			setFiles({});
			setValidationErrors({});
			formHydratedForCourseIdRef.current = undefined;
			forceExitPageRef.current = false;
			initialDataRef.current = pickFormFields(defaultFormData);
			initialCurriculumKeyRef.current = "";
			curriculumBaselineCapturedRef.current = false;
		}
	}, [courseId]);

	// Populate form data when course loads or switches; keep curriculum when the same course refetches
	useEffect(() => {
		if (!course) {
			return;
		}
		const isNewCourse = formHydratedForCourseIdRef.current !== course.id;
		formHydratedForCourseIdRef.current = course.id;

		forceExitPageRef.current = false;
		if (isNewCourse) {
			initialCurriculumKeyRef.current = "";
			curriculumBaselineCapturedRef.current = false;
		}

		const fields = formFieldsFromCourse(course);
		initialDataRef.current = fields;
		setFormData((prev) => ({
			...fields,
			curriculum: isNewCourse ? [] : prev.curriculum
		}));
	}, [course]);

	const validateForm = (): boolean => {
		const errors: Record<string, string> = {};

		if (!formData.title.trim()) {
			errors.title = t("admin_course_validation_title_required", "Title is required");
		} else if (formData.title.length > COURSE_TITLE_MAX_LENGTH) {
			errors.title = t("admin_course_validation_field_max_length", "{{field}} cannot exceed {{max}} characters.", {
				field: t("admin_course_field_title", "Title"),
				max: COURSE_TITLE_MAX_LENGTH
			});
		}
		if (formData.description.length > COURSE_DESCRIPTION_MAX_LENGTH) {
			errors.description = t("admin_course_validation_field_max_length", "{{field}} cannot exceed {{max}} characters.", {
				field: t("admin_course_field_description", "Description"),
				max: COURSE_DESCRIPTION_MAX_LENGTH
			});
		}
		if (!formData.contentType) {
			errors.contentType = t("admin_course_validation_content_type_required", "Content type is required");
		}
		if (formData.contentType === "Job Training") {
			if (!formData.workCategory?.length) {
				errors.workCategory = t(
					"admin_course_validation_work_category_job_training_required",
					"At least one work category is required for Job Training",
				);
			}
			if (!formData.jobCategory?.length) {
				errors.jobCategory = t(
					"admin_course_validation_job_category_job_training_required",
					"At least one job category is required for Job Training",
				);
			}
		}

		setValidationErrors(errors);

		// Expand the first section that has errors and scroll to validation message
		if (Object.keys(errors).length > 0) {
			const firstErrorSection =
				errors.title || errors.description
					? "1"
					: errors.contentType || errors.featuredImage || errors.jobCategory || errors.workCategory
						? "2"
						: "1";
			setExpandedSection(firstErrorSection);
			// Scroll validation message into view on next tick (after state updates)
			setTimeout(() => validationAlertRef.current?.scrollIntoView({behavior: "smooth", block: "start"}), 0);
		}

		return Object.keys(errors).length === 0;
	};

	const handleSave = async (includePublishDate = false) => {
		if (!validateForm()) {
			return;
		}

		const isJobTraining = formData.contentType === "Job Training";
		const courseData = {
			title: formData.title,
			description: formData.description,
			contentType: formData.contentType,
			linearProgression: formData.linearProgression,
			privacy: formData.privacy,
			locale: resolveLocale(appLocale),
			curriculum: toApiCurriculum(formData.curriculum),
			publishedAt: includePublishDate ? new Date().toISOString() : undefined,
			...(isJobTraining
				? {
					jobCategory: formData.jobCategory,
					workCategory: formData.workCategory
				}
				: {
					jobCategory: [] as string[],
					workCategory: [] as string[]
				})
		};

		// Extract the actual File from IDropzoneFile
		const featuredImage = files.featuredImage?.[0]?.file ?? undefined;

		try {
			const successMessage = `Course ${includePublishDate ? "saved and published" : "saved"} successfully`;
			if (isEditMode) {
				await updateMutation.mutateAsync({
					courseData,
					featuredImage
				});
				successSnackbar(successMessage);
				// Stay on edit page after update — re-snapshot the baseline so the page is no longer "dirty"
				initialDataRef.current = pickFormFields(formData);
				initialCurriculumKeyRef.current = curriculumKey(formData.curriculum);
				curriculumBaselineCapturedRef.current = true;
				setFiles({});
			} else {
				const result = await createMutation.mutateAsync({
					courseData,
					featuredImage
				});

				successSnackbar(successMessage);
				// Navigate to edit page after creation
				forceExitPageRef.current = true;
				navigate(`/admin/courses/${result.id}/edit-new`);
			}
		} catch (err) {
			errorSnackbar("Failed to save course");
		}
	};

	// Delete action commented out in favor of publish/unpublish
	// const handleDelete = async () => { ... };

	const handlePublishToggle = async () => {
		const isPublished = course && !!course.publishedAt;
		if (!isEditMode || (isEditMode && !isPublished)){
			await handleSave(true);
			return;
		}

		try {
			await updateMutation.mutateAsync({
				courseData: {
					publishedAt: null
				}
			});
			successSnackbar("Course successfully unpublish");
		} catch (err) {
			errorSnackbar("Failed to toggle publish");
		}
	};

	const handleCurriculumChange = useCallback((curriculum: IFlatListItem[]) => {
		setFormData((prev) => ({...prev, curriculum}));
		if (!curriculumBaselineCapturedRef.current) {
			initialCurriculumKeyRef.current = curriculumKey(curriculum);
			curriculumBaselineCapturedRef.current = true;
		}
	}, []);

	const hasUnsavedChanges = useCallback(() => {
		return (
			JSON.stringify(pickFormFields(formData)) !== JSON.stringify(initialDataRef.current) ||
			curriculumKey(formData.curriculum) !== initialCurriculumKeyRef.current ||
			!!files.featuredImage?.[0]?.file
		);
	}, [formData, files]);

	const blocker = useBlocker(() => hasUnsavedChanges() && !forceExitPageRef.current);

	useEffect(() => {
		if (blocker.state === "blocked") {
			setOpenExitPageModal(true);
		}
	}, [blocker]);

	// useBlocker doesn't cover browser refresh / tab close — handle via native prompt
	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent): boolean => {
			if (hasUnsavedChanges()) {
				event.preventDefault();
				event.returnValue = "";
				return true;
			}
			return false;
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [hasUnsavedChanges]);

	const handleExit = (): void => {
		blocker.proceed?.();
	};

	const handleKeepEditing = (): void => {
		blocker.reset?.();
		setOpenExitPageModal(false);
	};

	// Loading state
	if (isLoading) {
		return (
			<Box className={classes.root}>
				<Box className={classes.loadingContainer}>
					<CircularProgress />
				</Box>
			</Box>
		);
	}

	// Error state
	if (error) {
		return (
			<Box className={classes.root}>
				<Box className={classes.formContainer}>
					<Alert severity="error">Failed to load course: {getApiErrorMessage(error, "An error occurred")}</Alert>
				</Box>
			</Box>
		);
	}

	const isSaving = createMutation.isPending || updateMutation.isPending;

	return (
		<Box className={classes.root}>
			<Box className={classes.formContainer}>
				<AdminHeader
					rootSlug="/admin/courses"
					rootBreadcrumb={
						<>
							<IconAdminCourses />
							<Link to="/admin/courses">
								<Typography variant="body1">Courses</Typography>
							</Link>
						</>
					}
					editMode={courseId ? "edit" : "create"}
					entity={course as AdminHeaderEntity}
					type="course"
					title={isEditMode ? course?.title ?? "" : "Create New Course"}
					subtitle={isEditMode ? `Course ID: ${courseId}` : "Create a new training course"}
				/>
				{/* Validation errors */}
				{Object.keys(validationErrors).length > 0 && (
					<Box ref={validationAlertRef}>
						<Alert severity="error" sx={{mb: 2}}>
							{t(
								"admin_course_validation_fix_before_save",
								"Please fix the errors below before saving."
							)}
						</Alert>
					</Box>
				)}

				{/* Mutation errors */}
				{(createMutation.isError || updateMutation.isError) && (
					<Alert severity="error" sx={{mb: 2}}>
						{getApiErrorMessage(createMutation.error ?? updateMutation.error, "An error occurred")}
					</Alert>
				)}

				{/* Section 1: Course Information */}
				<Accordion
					expanded={expandedSection === "1"}
					onChange={(_, isExpanded) => setExpandedSection(isExpanded ? "1" : false)}
					className={classes.formFieldset}
				>
					<AccordionSummary
						className={classes.formFieldsetSummary}
						expandIcon={icon("expandMoreIcon")}
					>
						<Stack direction="row" alignItems="center">
							<span className={classes.formFieldsetNumber}>1</span>
							<Typography variant="h6" component="p">
								Course Information
							</Typography>
						</Stack>
						<Typography className={classes.subtitle}>
							Add the basic details for your course in English.
						</Typography>
					</AccordionSummary>
					<AccordionDetails className={classes.formFieldsetDetails}>
						<AdminInputText
							label={t("admin_course_field_title", "Title")}
							required
							value={formData.title}
							inputProps={{maxLength: COURSE_TITLE_MAX_LENGTH}}
							helperText={
								validationErrors.title
									? undefined
									: t("admin_course_char_count", "{{current}} / {{max}} characters", {
										current: formData.title.length,
										max: COURSE_TITLE_MAX_LENGTH
									})
							}
							FormHelperTextProps={{sx: {mt: 0.5}}}
							onChange={(e) => {
								setFormData((prev) => ({...prev, title: e.target.value}));
								if (validationErrors.title) {
									setValidationErrors((prev) => {
										const next = {...prev};
										delete next.title;
										return next;
									});
								}
							}}
							error={validationErrors.title}
						/>
						<AdminInputText
							label={t("admin_course_field_description", "Description")}
							multiline
							rows={5}
							value={formData.description}
							inputProps={{maxLength: COURSE_DESCRIPTION_MAX_LENGTH}}
							helperText={
								validationErrors.description
									? undefined
									: t("admin_course_char_count", "{{current}} / {{max}} characters", {
										current: formData.description.length,
										max: COURSE_DESCRIPTION_MAX_LENGTH
									})
							}
							FormHelperTextProps={{sx: {mt: 0.5}}}
							onChange={(e) => {
								setFormData((prev) => ({...prev, description: e.target.value}));
								if (validationErrors.description) {
									setValidationErrors((prev) => {
										const next = {...prev};
										delete next.description;
										return next;
									});
								}
							}}
							error={validationErrors.description}
						/>
					</AccordionDetails>
				</Accordion>

				{/* Section 2: Course Details */}
				<Accordion
					expanded={expandedSection === "2"}
					onChange={(_, isExpanded) => setExpandedSection(isExpanded ? "2" : false)}
					className={classes.formFieldset}
				>
					<AccordionSummary
						className={classes.formFieldsetSummary}
						expandIcon={icon("expandMoreIcon")}
					>
						<Stack direction="row" alignItems="center">
							<span className={classes.formFieldsetNumber}>2</span>
							<Typography variant="h6" component="p">
								Course Details
							</Typography>
						</Stack>
						<Typography className={classes.subtitle}>
							Choose a category and upload a thumbnail to represent your course.
						</Typography>
					</AccordionSummary>
					<AccordionDetails className={classes.formFieldsetDetails}>
						<Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
							<AdminInputSelect<string>
								label="Content Type"
								required
								value={formData.contentType}
								onChange={(e) => {
									const v = e.target.value;
									setFormData((prev) => ({...prev, contentType: v}));
									setValidationErrors((prev) => {
										const next = {...prev};
										delete next.contentType;
										if (v !== "Job Training") {
											delete next.jobCategory;
											delete next.workCategory;
										}
										return next;
									});
								}}
								options={contentTypes.map((type) => ({label: type, value: type}))}
								error={validationErrors.contentType}
							/>
							{formData.contentType === "Job Training" && (
								<>
									<FormControl sx={{minWidth: "200px"}} error={!!validationErrors.workCategory}>
										<InputLabel shrink id="work-cat-select-label">
											Work Category
										</InputLabel>
										<Select
											labelId="work-cat-select-label"
											id="work-cat-select"
											label="Work Category"
											multiple
											value={formData.workCategory}
											className={classes.select}
											onChange={(e) => {
												const {value} = e.target;
												setFormData((prev) => ({
													...prev,
													workCategory:
														typeof value === "string" ? value.split(",") : value
												}));
												if (validationErrors.workCategory) {
													setValidationErrors((prev) => {
														const next = {...prev};
														delete next.workCategory;
														return next;
													});
												}
											}}
											input={<OutlinedInput notched label="Work Category" />}
										>
											{workCategories.map((cat) => (
												<MenuItem key={cat} value={cat}>
													{cat}
												</MenuItem>
											))}
										</Select>
										{validationErrors.workCategory ? (
											<FormHelperText>{validationErrors.workCategory}</FormHelperText>
										) : null}
									</FormControl>
									<FormControl sx={{minWidth: "200px"}} error={!!validationErrors.jobCategory}>
										<InputLabel shrink id="job-cat-select-label">
											Job Category
										</InputLabel>
										<Select
											labelId="job-cat-select-label"
											id="job-cat-select"
											label="Job Category"
											multiple
											value={formData.jobCategory}
											className={classes.select}
											onChange={(e) => {
												const {value} = e.target;
												setFormData((prev) => ({
													...prev,
													jobCategory:
														typeof value === "string" ? value.split(",") : value
												}));
												if (validationErrors.jobCategory) {
													setValidationErrors((prev) => {
														const next = {...prev};
														delete next.jobCategory;
														return next;
													});
												}
											}}
											input={<OutlinedInput notched label="Job Category" />}
										>
											{jobCategories.map((cat) => (
												<MenuItem key={cat} value={cat}>
													{cat}
												</MenuItem>
											))}
										</Select>
										{validationErrors.jobCategory ? (
											<FormHelperText>{validationErrors.jobCategory}</FormHelperText>
										) : null}
									</FormControl>
								</>
							)}
						</Box>
						<ImageSelector
							label="Featured Image"
							error={validationErrors.featuredImage}
							defaultImageUrl={course?.featuredImage?.url}
							onImageSelected={(selectedFiles) => {
								setFiles((prev) => ({
									...prev,
									featuredImage: selectedFiles ?? null
								}));
								if (validationErrors.featuredImage && selectedFiles?.length) {
									setValidationErrors((prev) => {
										const next = {...prev};
										delete next.featuredImage;
										return next;
									});
								}
							}}
						/>
					</AccordionDetails>
				</Accordion>

				{/* Section 3: Curriculum */}
				<Accordion
					expanded={expandedSection === "3"}
					onChange={(_, isExpanded) => setExpandedSection(isExpanded ? "3" : false)}
					className={classes.formFieldset}
				>
					<AccordionSummary
						className={classes.formFieldsetSummary}
						expandIcon={icon("expandMoreIcon")}
					>
						<Stack direction="row" alignItems="center">
							<span className={classes.formFieldsetNumber}>3</span>
							<Typography variant="h6" component="p">
								Add Lessons
							</Typography>
						</Stack>
						<Typography className={classes.subtitle}>
							Start building your course by adding new content or importing
							existing lessons. You can include up to 20 lessons.
						</Typography>
					</AccordionSummary>
					<AccordionDetails className={classes.formFieldsetDetails}>
						<AdminCourseCurriculum
							courseId={courseId}
							initialData={course}
							onChange={handleCurriculumChange}
						/>
					</AccordionDetails>
				</Accordion>

				{/* Section 4: Course Settings */}
				<Accordion
					expanded={expandedSection === "4"}
					onChange={(_, isExpanded) => setExpandedSection(isExpanded ? "4" : false)}
					className={classes.formFieldset}
				>
					<AccordionSummary
						className={classes.formFieldsetSummary}
						expandIcon={icon("expandMoreIcon")}
					>
						<Stack direction="row" alignItems="center">
							<span className={classes.formFieldsetNumber}>4</span>
							<Typography variant="h6" component="p">
								Course Settings
							</Typography>
						</Stack>
						<Typography className={classes.subtitle}>
							Customize how this course is shared, assigned, and interacted with.
						</Typography>
					</AccordionSummary>
					<AccordionDetails
						className={cx(classes.formFieldsetDetails, classes.fieldsetSettings)}
					>
						<div className={classes.divider} />
						<AdminInputSwitch
							label="Share across other companies"
							checked={formData.privacy === "global"}
							onChange={(e) =>
								setFormData((prev) => ({
									...prev,
									privacy: e.target.checked ? "global" : "cohort"
								}))
							}
						/>
						<div className={classes.divider} />
						<AdminInputSwitch
							label="Lock next lesson until previous is done"
							checked={formData.linearProgression}
							onChange={() =>
								setFormData((prev) => ({
									...prev,
									linearProgression: !prev.linearProgression
								}))
							}
						/>
					</AccordionDetails>
				</Accordion>
				{/* Actions - Sticky footer */}
				<Box className={classes.actions}>
					<Button
						variant="contained"
						onClick={handlePublishToggle}
						disabled={isSaving || updateMutation.isPending}
					>
						{updateMutation.isPending ? (
							<CircularProgress size={20} />
						) : course?.publishedAt ? (
							"Unpublish"
						) : (
							"Publish"
						)}
					</Button>
					{saveButtonLabel && (
						<Button
							variant="outlined"
							color="primary"
							onClick={() => handleSave()}
							disabled={isSaving || updateMutation.isPending}
						>
							{isSaving ? <CircularProgress size={20} /> : saveButtonLabel}
						</Button>
					)}
					<Button
						variant="text"
						onClick={() => {
							forceExitPageRef.current = true;
							navigate("/admin/courses");
						}}
						disabled={isSaving}
						sx={{marginLeft: "auto"}}
						className={classes.cancelBtn}
					>
						Cancel
					</Button>
				</Box>
				{/* Delete action commented out
					{isEditMode && (
						<Button variant="outlined" color="error" ... onClick={handleDelete}>Delete</Button>
					)}
					*/}
			</Box>
			{openExitPageModal && (
				<ConfirmationDialog
					title={t("common_unsaved_data_title", "Unsaved data will be lost")}
					content={t("common_unsaved_changes_message", "You'll lose any unsaved work if you exit now. Do you want to proceed?")}
					testIdPrefix="unsavedChanges"
					open={openExitPageModal}
					hideCloseIcon
					cancelButtonTitle={t("common_exit", "Exit")}
					confirmButtonTitle={t("common_keep_editing", "Keep Editing")}
					onCancel={handleExit}
					onConfirm={handleKeepEditing}
					preventBackDropClick
				/>
			)}
		</Box>
	);
};
